import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { getTimeStampParts } from "src/lib/utils/timestamp-parts";

interface ClipControlProps {
  disabled?: boolean;
  handleClick: () => void;
}

const CLIP_CONTROL_STYLES =
  "inline-flex justify-center items-center w-8 h-8 text-3xl text-red-400 disabled:text-gray-500 leading-none";

export function Play({ disabled, handleClick }: ClipControlProps) {
  return (
    <button aria-label="Play" className={`${CLIP_CONTROL_STYLES} !text-4xl`} disabled={disabled} onClick={handleClick}>
      <span style={{ transform: "scaleX(0.75)" }}>&#9658;</span>
    </button>
  );
}

export function Pause({ handleClick }: ClipControlProps) {
  return (
    <button aria-label="Pause" className={CLIP_CONTROL_STYLES} onClick={handleClick}>
      <span style={{ transform: "scaleX(0.75)" }}>&#10074;&#10074;</span>
    </button>
  );
}

export function SkipBack({ disabled, handleClick }: ClipControlProps) {
  return (
    <button aria-label="Jump to start" className={CLIP_CONTROL_STYLES} disabled={disabled} onClick={handleClick}>
      <span style={{ transform: "scaleX(-0.75)" }}>&#9658;&#10073;</span>
    </button>
  );
}

export function SkipForward({ disabled, handleClick }: ClipControlProps) {
  return (
    <button aria-label="Jump to end" className={CLIP_CONTROL_STYLES} disabled={disabled} onClick={handleClick}>
      <span style={{ transform: "scaleX(0.75)" }}>&#9658;&#10073;</span>
    </button>
  );
}

export interface TimeStampProps {
  min: number;
  max: number;
  time: number;
  handleTimeChange: (time: number) => void;
}

const TIME_DIGIT_LIMITS = [99, 59, 59];

export function TimeStamp({ min, max, time, handleTimeChange }: TimeStampProps) {
  const [timeStamp, _setTimeStamp] = useState("00:00:00");
  const timeStampRef = useRef(timeStamp);
  const setTimeStamp = (t: string) => {
    timeStampRef.current = t;
    _setTimeStamp(t);
  };
  const [hours, minutes, seconds] = timeStamp.split(":");

  useEffect(() => {
    const { h, m, s } = getTimeStampParts(time);
    setTimeStamp(`${h}:${m}:${s}`);
  }, [time]);

  const updateTime = (content: string, digitIndex: 0 | 1 | 2, forceUpdate = false) => {
    const limit = TIME_DIGIT_LIMITS[digitIndex];
    const value = parseInt(content);
    if (Number.isNaN(value) || value > limit) {
      return;
    }
    const digits = timeStampRef.current.split(":");
    if (!forceUpdate) {
      const tempDigits = digits.slice();
      tempDigits.splice(digitIndex, 1, value.toString().padStart(2, "0"));
      setTimeStamp(tempDigits.join(":"));
      return;
    }
    const newValue = Math.min(Math.max(0, value), limit);
    const newTime = digits.reduce((acc, curr, i) => {
      const multiplier = i === 2 ? 1000 : i === 1 ? 60_000 : 3_600_000;
      if (i === digitIndex) {
        acc += newValue * multiplier;
      } else {
        acc += parseInt(curr) * multiplier;
      }
      return acc;
    }, 0);
    const updatedTime = Math.min(max, Math.max(min, newTime));
    const { h, m, s } = getTimeStampParts(updatedTime);
    setTimeStamp(`${h}:${m}:${s}`);
    handleTimeChange(updatedTime);
  };

  const confirmTime = (event: KeyboardEvent<HTMLInputElement>, digitIndex: 0 | 1 | 2) => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    updateTime(event.target.value, digitIndex, true);
  };

  return (
    <div className="flex items-center p-1 text-gray-500 border-solid border-2 border-gray-100">
      <input
        className="w-5 text-center"
        type="text"
        value={hours}
        onChange={(e) => updateTime(e.target.value, 0)}
        onBlur={(e) => updateTime(e.target.value, 0, true)}
        onKeyDown={(e) => confirmTime(e, 0)}
      />
      :
      <input
        className="w-5 text-center"
        type="text"
        value={minutes}
        onChange={(e) => updateTime(e.target.value, 1)}
        onBlur={(e) => updateTime(e.target.value, 1, true)}
        onKeyDown={(e) => confirmTime(e, 1)}
      />
      :
      <input
        className="w-5 text-center"
        type="text"
        value={seconds}
        onChange={(e) => updateTime(e.target.value, 2)}
        onBlur={(e) => updateTime(e.target.value, 2, true)}
        onKeyDown={(e) => confirmTime(e, 2)}
      />
    </div>
  );
}
